// TelemetryStats.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import Spinner from '../../../util/spinner-shim'
import _sumBy from 'lodash/sumBy'
import { useLocation } from 'react-router-dom'

import {
  chartDimensions,
  displayFormula,
} from 'altrac-conversions'

import {
  IconLink,
}
  from '../Navigation/Buttons'

import NoChartData from './NoChartData'
import styles from '../../../../css/charts.module.css'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'
import ChartDataLoading from './ChartDataLoading'

function makeColumns (label) {
  return ([
    {
      type: 'date',
      label: 'Time',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label,
    },
  ])
}

function makeRows (statsData, label, statType, formula, physical, context, multiplier, precision) {
  const rows = []
  if (
    statsData.length > 0
  ) {
    for (let i = 0; i < statsData.length; i += 1) {
      const row = []
      const currentSensorData = statsData[i].stats
      let lastSensorData = statsData[i].stats
      if (i > 0) {
        lastSensorData = statsData[i - 1].stats
      }
      if (statsData[i].stats[statType] || statsData[i].stats[statType] === 0) {
        let value = statsData[i].stats[statType]
        if (i < statsData.length - 1) {
          lastSensorData = statsData[i + 1]
        }
        if (formula) {
          value = displayFormula(
            formula,
            multiplier,
            precision,
            context,
            statType,
            currentSensorData || {},
            lastSensorData || {},
            physical || {}
          )
          if (value === 'OFF' || value === 'Off' || value === 'STP' || value === 'ERL' || value === 'ERH') {
            value = 0
          } else if (value === 'ON' || value === 'RUN' || value === 'Low') {
            value = 1
          } else if (value === 'High') {
            value = 2
          }
        }
        // since we only do whole days in stats mode, we shift date by 12 hours in order to show
        // in the right place
        const dateKeeper = new Date(statsData[i].date_begin)
        dateKeeper.setHours(dateKeeper.getHours() + 12)
        row[0] = dateKeeper
        row[1] = `
          <div style='padding:0px 10px;min-width:160px;'>
            <h6>${dateKeeper.toLocaleDateString()}</h6>
            <h6>${label}: ${(value || value === 0) ? value : 'Error'}</h6>
          </div>
        `
        row[2] = value
        rows.push(row)
      }
    }
  }
  return rows
}

const TableCell = function TableCell ({ value }) {
  const returnVal = (
    <td>
      {value}
    </td>
  )
  return returnVal
}

TableCell.propTypes = {
  value: PropTypes.node,
}

const Summary = function Summary ({
  statsData,
  valueKey,
  multiplier,
  label,
  precision,
  formula,
}) {
  const sum = _sumBy(statsData, o => o.stats.sum)
  // const value = gallonsToAcreFeet(sum / multiplier, 2);
  const value = displayFormula(
    formula,
    multiplier,
    precision,
    null,
    'sum',
    { sum },
    null,
    null
  )

  return (
    <IconLink
      key={valueKey}
      path={null}
      label={label}
      value={value}
      unit=''
      width='triple'
    />
  )
}

const TableRow = function TableRow ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    if (i !== 1) {
      let cellValue
      if (data[i] !== null && typeof data[i].getMonth === 'function') {
        cellValue = data[i].toLocaleDateString()
      } else if (data[i] !== null) {
        cellValue = data[i]
      } else {
        cellValue = 'N/A'
      }
      cells.push(
        <TableCell key={i} value={cellValue} />
      )
    }
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

TableRow.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({
  statsData,
  label,
  physical,
  statType,
  formula,
  multiplier,
  precision,
}) {
  const { tempConv } = AppStore.getUser()?.locale || { tempConv: 'c' }

  if (statsData.length < 1) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const rows = makeRows(
    statsData,
    label,
    statType,
    formula,
    physical,
    { tempConv },
    multiplier,
    precision
  )
  const rowsArr = []
  for (let i = 0; i < rows.length; i += 1) {
    rowsArr.push(
      <TableRow key={i} data={rows[i]} />
    )
  }
  const columns = makeColumns(label)
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <div className='telemetryTableWrapper'>
      <table className='table table-striped'>
        <thead>
          <TableRow data={columnArr} />
        </thead>
        <tbody>
          {rowsArr}
        </tbody>
      </table>
    </div>
  )
}

Table.propTypes = {
  statsData: PropTypes.arrayOf(PropTypes.object),
  physical: PropTypes.object,
  statType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  multiplier: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  precision: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  formula: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
}

export const ChartStats = function ChartStats ({
  statsData,
  statsReady,
  physical,
  statType,
  formula,
  label,
  multiplier,
  precision,
  vMax,
  vMin,
  chartType,
}) {
  const { tempConv } = AppStore.getUser()?.locale || { tempConv: 'c' }

  if (
    statsReady !== 'loaded' ||
    typeof statsData === 'undefined'
  ) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  const options = {
    title: 'Stats',
    hAxis: {
      slantedText: false,
      gridlines: {
        count: -1,
        units: {
          days: { format: ['M/d'] },
          hours: { format: ['M/d ha'] },
        },
      },
    },
    vAxis: {
      title: label,
      textPosition: 'in',
    },
    series: {
      0: {
        targetAxisIndex: 1,
        color: '#50b0ff',
        lineWidth: 4,
      },
    },
    chartArea: {
      left: 0,
      top: 0,
      width: '100%',
      height: '85%',
    },
    isStacked: false,
    legend: { position: 'bottom' },
    lineWidth: 4,
    // This line makes the entire category's tooltip active.
    focusTarget: 'category',
    // Use an HTML tooltip.
    tooltip: {
      isHtml: true,
      trigger: 'focus',
    },
  }

  if (vMax !== null) {
    options.vAxis.maxValue = vMax
  }

  if (vMin !== null) {
    options.vAxis.minValue = vMin
  }

  const rows = makeRows(
    statsData,
    label,
    statType,
    formula,
    physical,
    { tempConv },
    multiplier,
    precision
  )
  if (rows.length < 1) {
    return (
      <NoChartData />
    )
  }

  const columns = makeColumns(label)

  const dimensions = chartDimensions(window.innerWidth)

  return (
    <div className={styles.chart}>
      <Chart
        chartType='ColumnChart'
        rows={rows}
        width={dimensions.width}
        height={dimensions.height}
        columns={columns}
        options={options}
        style={{
          margin: '0 auto',
          position: 'relative',
          top: '5px',
        }}
      />
    </div>
  )
}

ChartStats.propTypes = {
  statsData: PropTypes.arrayOf(PropTypes.object),
  physical: PropTypes.object,
  statsReady: PropTypes.string,
  statType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  formula: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  multiplier: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  precision: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  vMax: PropTypes.number,
  vMin: PropTypes.number,
  chartType: PropTypes.string,
}

function TelemetryStats (props) {
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const valueKeyParam = useMemo(() => searchParams.get('valueKey'), [searchParams])

  const {
    device,
    periodStart,
    periodStop,
    statsData,
    statsReady,
  } = props

  const getNewData = useCallback((address, start, stop, valueKey) => {
    let periodStartDate = start || periodStart
    periodStartDate = periodStartDate.substr(0, 10)
    let periodStopDate = stop || periodStop
    periodStopDate = periodStopDate.substr(0, 10)
    AppDispatcher.dispatch({
      action: 'get-stats-address',
      address: address || device.address,
      timeBeg: periodStartDate,
      timeEnd: periodStopDate,
      valueKey: valueKey || valueKeyParam,
    })
  }, [device.address, periodStart, periodStop, valueKeyParam])

  useEffect(() => {
    const interval = setInterval(getNewData, 60000)
    return () => clearInterval(interval)
  }, [getNewData])

  useEffect(() => {
    getNewData(null, periodStart, periodStop, valueKeyParam)
  }, [periodStart, periodStop, valueKeyParam, getNewData])

  if (
    !Array.isArray(statsData) ||
    statsData.length < 1 ||
    statsReady !== 'loaded'
  ) {
    return (
      <ChartDataLoading />
    )
  }

  let formula = ''
  if (searchParams.get('formula')) {
    formula = searchParams.get('formula')
  }

  let statType = ''
  if (searchParams.get('statType')) {
    statType = searchParams.get('statType')
  }

  let precision = 0
  if (searchParams.get('precision')) {
    const precisionParam = searchParams.get('precision')
    if (!isNaN(precisionParam) || precisionParam.indexOf(',') > -1) {
      precision = Number(precisionParam)
    }
  }

  let label = 'Stat'
  if (searchParams.get('label')) {
    label = searchParams.get('label')
  }

  let multiplier = 1
  if (searchParams.get('multiplier')) {
    const multiplierParam = searchParams.get('multiplier')
    if (!isNaN(multiplierParam) || multiplierParam.indexOf(',') > -1) {
      multiplier = Number(multiplierParam)
    }
  }

  let vMax = null
  if (searchParams.get('vMax')) {
    const vMaxParam = searchParams.get('vMax')
    if (!isNaN(vMaxParam)) {
      vMax = Number(vMaxParam)
    }
  }

  let vMin = null
  if (searchParams.get('vMin')) {
    const vMinParam = searchParams.get('vMin')
    if (!isNaN(vMinParam)) {
      vMin = Number(vMinParam)
    }
  }

  let chartType = 'AreaChart'
  if (searchParams.get('chartType')) {
    chartType = searchParams.get('chartType')
  }

  return (
    <div className='deviceInfo'>
      <ChartStats
        statsData={statsData}
        statsReady={statsReady}
        physical={device.physical}
        statType={statType}
        formula={formula}
        label={label}
        multiplier={multiplier}
        precision={precision}
        vMax={vMax}
        vMin={vMin}
        chartType={chartType}
      />
      <Summary
        statsData={statsData}
        formula={formula}
        precision={precision}
        label={label}
        multiplier={multiplier}
        valueKey={searchParams.get('valueKey')}
      />
      <br />
      <br />
      <Table
        statsData={statsData}
        physical={device.physical}
        statType={statType}
        formula={formula}
        label={label}
        multiplier={multiplier}
        precision={precision}
      />
    </div>
  )
}

TelemetryStats.propTypes = {
  device: PropTypes.shape({
    address: PropTypes.string,
    physical: PropTypes.object,
  }),
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  statsData: PropTypes.arrayOf(PropTypes.object),
  statsReady: PropTypes.string,
}

export default TelemetryStats
